/**
 * useSharing — TanStack Query hooks for the "who is this entity shared with"
 * CRUD used by BOTH ShareModal.tsx's legacy branch and ShareSheet.tsx.
 *
 * Spec 296 Package E open question, resolved: `ShareModal.tsx` is NOT a
 * separate live UX pattern from `ShareSheet.tsx` — it's a feature-flagged
 * compatibility shim (`unifiedSharing`, see ShareModal.tsx's file doc).
 * When the flag is on, ShareModal renders ShareSheet directly (mode="modal").
 * When off, `LegacyShareModal` — ShareModal's own hand-rolled fetch/share/
 * remove implementation — is what actually renders. Spec 247's feature-flag
 * audit (docs/specs/implemented/247-feature-flag-tier3-batch3-group-c.md)
 * independently confirmed this is a "genuine kill-switch... both reachable,"
 * and the flag's migration seeded `default_enabled=false` — so the legacy
 * branch is reachable by construction, not merely a vestigial fallback (per-
 * org runtime state is override/tier-resolved server-side and isn't
 * something this migration can verify from the repo alone).
 *
 * Because both branches are real, BOTH ShareModal.tsx (4 entity types:
 * journal/scene/media/album) and ShareSheet.tsx (6: adds inventory/
 * scenePlan) need their existing-shares fetch + share/remove actions
 * migrated — not just ShareSheet's. Rather than duplicate near-identical
 * switch-by-entityType fetch/mutate logic in both files, this module holds
 * ONE shared implementation, keyed on ShareSheet's superset `SharingEntityType`
 * (ShareModal's narrower type is structurally assignable into it — every
 * value ShareModal ever passes is already a member of this union).
 *
 * Behavior note: this file's fetch normalization (`raw?.data ?? raw`, then
 * `.shares` or a bare array) is ShareSheet's pre-migration logic, which is
 * STRICTLY MORE PERMISSIVE than ShareModal's pre-migration one
 * (`response?.data?.data?.shares || []`) — the shared fetcher can only
 * return MORE rows than ShareModal's old one for the same response shape,
 * never fewer, so reusing it is behavior-safe. Named here because it's a
 * (harmless) behavior widening for ShareModal's legacy branch, not a no-op.
 */
import { useQuery, useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { journalAPI, mediaAPI, scenePlanAPI } from '../../services/api';
import api from '../../services/api/client';
import { queryKeys } from './queryKeys';

export type SharingEntityType = 'journal' | 'scene' | 'media' | 'album' | 'inventory' | 'scenePlan';
export type SharePermission = 'view' | 'edit';

export interface ExistingShare {
  id: number;
  sharedWithUserId: number;
  sharedWithUserName: string;
  sharedWithUserEmail?: string;
  sharedByUserName?: string;
  permission: string;
  createdAt: string;
}

const NO_SHARES: ExistingShare[] = [];

async function fetchShares(entityType: SharingEntityType, entityId: string): Promise<ExistingShare[]> {
  let response;
  switch (entityType) {
    case 'journal': response = await journalAPI.getEntryShares(entityId); break;
    case 'scene': response = await journalAPI.getSceneShares(entityId); break;
    case 'media': response = await mediaAPI.getItemShares(entityId); break;
    case 'album': response = await mediaAPI.getAlbumShares(parseInt(entityId, 10)); break;
    case 'inventory': response = await api.get(`/inventory/${entityId}/shares`); break;
    case 'scenePlan': response = await scenePlanAPI.getShares(entityId); break;
  }
  const raw = response?.data;
  // Normalize: some endpoints wrap in { data: { shares: [...] } }, others in { data: [...] }
  const unwrapped = raw?.data ?? raw;
  return unwrapped?.shares || (Array.isArray(unwrapped) ? unwrapped : []);
}

/**
 * Existing shares for one entity. `enabled` lets the caller gate on its own
 * "should I even fetch" condition (ShareSheet: `isActive && !isCreateMode`;
 * ShareModal: `isOpen && !!entityId`) without duplicating that logic here.
 */
export function useExistingSharesQuery(
  entityType: SharingEntityType,
  entityId: string | undefined,
  enabled: boolean,
) {
  const query = useQuery<ExistingShare[]>({
    queryKey: queryKeys.sharing.shares(entityType, entityId || ''),
    queryFn: () => fetchShares(entityType, entityId as string),
    enabled: enabled && !!entityId,
    staleTime: 30_000,
    // Pre-migration code (both files) was a single try/catch'd fetch with no
    // retry wrapper — opting out explicitly rather than inheriting the
    // global QueryClient's retry:2 default (same fix as every prior file in
    // this migration series).
    retry: false,
  });
  // Explicit annotation works around this TS version's inference gap: without
  // it, `query.data ?? NO_SHARES` collapses to `any` inside the object
  // literal, and every downstream consumer's .map/.filter/.find callback
  // parameter loses its type (same class of gap documented on the
  // setQueryData updaters elsewhere in this migration).
  const data: ExistingShare[] = query.data ?? NO_SHARES;
  return { ...query, data };
}

export interface CreateShareVars {
  entityType: SharingEntityType;
  entityId: string;
  memberIds: number[];
  permission: SharePermission;
  /** Required for 'media'/'album' only (mirrors pre-migration orgId parsing). */
  orgId?: number;
}

/**
 * Share an entity with the given members. Invalidates the shares query for
 * this exact entity on success — matches this pair's pre-migration behavior
 * of an immediate `loadExistingShares()` re-fetch after a successful share,
 * not an optimistic local splice (unlike useMessages.ts's precedent) because
 * the pre-migration code always re-fetched here rather than assembling the
 * new share row itself (the server computes sharedWithUserName/Email, which
 * the client doesn't have on hand for a plain member id).
 */
export function useCreateShareMutation(): UseMutationResult<unknown, unknown, CreateShareVars> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entityType, entityId, memberIds, permission, orgId }: CreateShareVars) => {
      switch (entityType) {
        case 'journal': return journalAPI.shareEntry(entityId, memberIds, permission);
        case 'scene': return journalAPI.shareScene(entityId, memberIds, permission);
        case 'media': return mediaAPI.shareItem(entityId, orgId ?? 0, memberIds);
        case 'album': return mediaAPI.shareAlbum(parseInt(entityId, 10), orgId ?? 0, memberIds);
        case 'inventory': return api.post(`/inventory/${entityId}/share`, { memberIds, permission });
        case 'scenePlan': return scenePlanAPI.share(entityId, memberIds, permission);
      }
    },
    onSuccess: (_data, { entityType, entityId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sharing.shares(entityType, entityId) });
    },
  });
}

export interface RemoveShareVars {
  entityType: SharingEntityType;
  entityId: string;
  userId: number;
}

/** Remove one member's access. Same invalidate-on-success shape as create. */
export function useRemoveShareMutation(): UseMutationResult<unknown, unknown, RemoveShareVars> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entityType, entityId, userId }: RemoveShareVars) => {
      switch (entityType) {
        case 'journal': return journalAPI.removeEntryShare(entityId, userId);
        case 'scene': return journalAPI.removeSceneShare(entityId, userId);
        case 'media': return mediaAPI.removeItemShare(entityId, userId);
        case 'album': return mediaAPI.removeAlbumShare(parseInt(entityId, 10), userId);
        case 'inventory': return api.delete(`/inventory/${entityId}/shares/${userId}`);
        case 'scenePlan': return scenePlanAPI.removeShare(entityId, userId);
      }
    },
    onSuccess: (_data, { entityType, entityId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sharing.shares(entityType, entityId) });
    },
  });
}
